import React, { useState, useContext, useEffect, useRef } from "react";
import styles from "../styles/AccountPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faKey,
  faTrash,
  faSave,
  faEye,
  faEyeSlash,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../AuthProvider";
import { AvatarContext } from "../contexts/AvatarContext";
import { UserContext } from "../contexts/UserContext";
import {
  getCurrentUserInfo,
  updateUserInfo,
  updatePassword,
  deleteAccount,
  uploadAvatar,
} from "../api/api";
import { useSnackbar } from "notistack";

const Account = () => {
  const { keycloak } = useContext(AuthContext);
  const { setAvatarUrl } = useContext(AvatarContext);
  const { userInfo, setUserInfo } = useContext(UserContext);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [organization, setOrganization] = useState("");
  const [email, setEmail] = useState("");
  const [avatar, setAvatar] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const hideRequirementsTimeoutRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const data = await getCurrentUserInfo(keycloak.token);
        setFirstName(data.first_name);
        setLastName(data.last_name);
        setUsername(data.username || "");
        setOrganization(data.organization);
        setEmail(data.email);
        setAvatar(data.picture);
        setAvatarUrl(data.picture);
        setUserInfo({
          firstName: data.first_name,
          lastName: data.last_name,
          username: data.username,
          organization: data.organization,
          email: data.email,
          picture: data.picture,
        });
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };

    fetchUserInfo();
  }, []);

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        "image/png",
        "image/webp",
        "image/jpeg",
        "image/svg+xml",
      ];
      const maxSize =
        file.type === "image/svg+xml" ? 300 * 1024 : 1 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        enqueueSnackbar(
          "Invalid file type. Only PNG, WEBP, JPG, and SVG are allowed.",
          {
            variant: "error",
          }
        );
        return;
      }

      if (file.size > maxSize) {
        enqueueSnackbar(
          `File size exceeds the limit. Max size is ${maxSize / 1024}KB.`,
          {
            variant: "error",
          }
        );
        return;
      }

      setIsSaving(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const response = await uploadAvatar(keycloak.token, formData);
        if (response) {
          const avatarUrl = URL.createObjectURL(file);
          setAvatar(avatarUrl);
          setAvatarUrl(avatarUrl);
          enqueueSnackbar("Avatar uploaded successfully!", {
            variant: "success",
          });
        } else {
          enqueueSnackbar("Error uploading avatar!", {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar("Failed to upload avatar. Please try again.", {
          variant: "error",
        });
      } finally {
        setIsSaving(false);
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Save user details
  const saveAccountDetails = async () => {
    // Validate username if provided
    if (username && !validateUsername(username)) {
      enqueueSnackbar("Username must be 3-20 characters long and contain only letters, numbers, and underscores.", {
        variant: "error",
      });
      return;
    }

    setIsSaving(true);
    let userInfo = {
      first_name: firstName,
      last_name: lastName,
      username: username,
      organization: organization,
      email: email,
    };
    const token = keycloak.token;
    try {
      const response = await updateUserInfo(token, userInfo);
      if (!response) {
        enqueueSnackbar("Error updating user details!", {
          variant: "error",
        });
      } else {
        enqueueSnackbar("User details updated successfully!", {
          variant: "success",
        });
        setUserInfo({
          firstName,
          lastName,
          username,
          organization,
          email,
          picture: avatar,
        });
      }
    } catch (error) {
      enqueueSnackbar("Failed to save account details. Please try again.", {
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const validateUsername = (username) => {
    // Username should be 3-20 characters, alphanumeric and underscores only
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    return usernameRegex.test(username);
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return (
      password.length >= minLength &&
      hasUpperCase &&
      hasLowerCase &&
      hasNumber &&
      hasSpecialChar
    );
  };

  const getPasswordRequirements = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      minLength: password.length >= minLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar,
      passwordsMatch: password && confirmPassword && password === confirmPassword,
    };
  };

  const handlePasswordFocus = () => {
    // Clear any pending timeout to hide the requirements
    if (hideRequirementsTimeoutRef.current) {
      clearTimeout(hideRequirementsTimeoutRef.current);
      hideRequirementsTimeoutRef.current = null;
    }
    setShowPasswordRequirements(true);
  };

  const handlePasswordBlur = () => {
    // Add a small delay to allow button clicks to register before hiding the display
    hideRequirementsTimeoutRef.current = setTimeout(() => {
      setShowPasswordRequirements(false);
    }, 150);
  };

  // Change password
  const changePassword = async () => {
    if (newPassword !== confirmPassword) {
      enqueueSnackbar("Passwords do not match!", {
        variant: "error",
      });
      return;
    }
    if (!validatePassword(newPassword)) {
      enqueueSnackbar(
        "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.",
        {
          variant: "error",
        }
      );
      return;
    }
    setIsSaving(true);
    const token = keycloak.token;
    try {
      const result = await updatePassword(token, newPassword);
      if (result) {
        enqueueSnackbar("Password updated successfully!", {
          variant: "success",
        });
        setNewPassword("");
        setConfirmPassword("");
      } else {
        enqueueSnackbar("Error updating password!", {
          variant: "error",
        });
      }
    } catch (error) {
      if (error.response && error.response.data && 
          error.response.data.error && 
          error.response.data.error.includes("old password")
        ) {
        enqueueSnackbar("You cannot use the same password again", {
          variant: "error",
        });
      } else {
        enqueueSnackbar("Failed to update password. Please try again.", {
          variant: "error",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // // Toggle Two-Factor Authentication
  // const toggle2FA = () => {
  //   setIs2FAEnabled(!is2FAEnabled);
  //   alert(
  //     `Two-Factor Authentication ${!is2FAEnabled ? "Enabled" : "Disabled"}`
  //   );
  // };

  // Delete account
  const deleteUserAccount = async () => {
    setShowDeleteModal(false);
    setIsSaving(true);
    const token = keycloak.token;
    try {
      const result = await deleteAccount(token);
      if (result) {
        enqueueSnackbar("Account deleted successfully!", {
          variant: "success",
        });
        keycloak.logout({ redirectUri: window.location.origin });
      } else {
        enqueueSnackbar("Error deleting account!", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Failed to delete account. Please try again.", {
        variant: "error",
      });
    } finally {
      setIsSaving(false);
      setDeleteConfirmText("");
    }
  };

  return (
    <div className={styles.accountContainer}>
      <h2>Account Settings</h2>

      {/* Personal Info */}
      <div className={styles.section}>
        <h3>
          <FontAwesomeIcon icon={faUser} /> Personal Information
        </h3>
        <div className={styles.inputGroup}>
          <label>First Name:</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div className={styles.inputGroup}>
          <label>Last Name:</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
        <div className={styles.inputGroup}>
          <label>Username:</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter your username"
          />
          <small className={styles.inputHint}>
            3-20 characters, letters, numbers, and underscores only
          </small>
        </div>
        <div className={styles.inputGroup}>
          <label>Organization:</label>
          <input
            type="text"
            value={organization}
            onChange={(e) => setOrganization(e.target.value)}
          />
        </div>
        <div className={styles.inputGroup}>
          <label>Email:</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className={styles.inputGroup}>
          <label>Avatar:</label>
          <button
            className={styles.uploadButton}
            onClick={triggerFileInput}
            disabled={isSaving}
          >
            <FontAwesomeIcon icon={faUpload} /> Upload Avatar
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleAvatarChange}
          />
          <p className={styles.uploadLimitations}>
            Supported formats: PNG, WEBP, JPG (up to 1MB), SVG (up to 300KB)
          </p>
        </div>
        <button
          className={styles.saveButton}
          onClick={saveAccountDetails}
          disabled={isSaving}
        >
          <FontAwesomeIcon icon={faSave} /> Save Changes
        </button>
      </div>

      {/* Change Password */}
      <div className={styles.section}>
        <h3>
          <FontAwesomeIcon icon={faKey} /> Change Password
        </h3>

        {/* New Password Field with Toggle */}
        <div className={styles.passwordGroup}>
          <label>New Password:</label>
          <div className={styles.passwordField}>
            <input
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onFocus={handlePasswordFocus}
              onBlur={handlePasswordBlur}
            />
            <button
              className={styles.showPasswordButton}
              onClick={togglePasswordVisibility}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
          </div>
        </div>

        {/* Confirm Password Field with Toggle */}
        <div className={styles.passwordGroup}>
          <label>Confirm Password:</label>
          <div className={styles.passwordField}>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onFocus={handlePasswordFocus}
              onBlur={handlePasswordBlur}
            />
            <button
              className={styles.showPasswordButton}
              onClick={togglePasswordVisibility}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
          </div>
        </div>

        {/* Password Requirements Display */}
        {showPasswordRequirements && (
          <div className={styles.passwordRequirements}>
            <h4>Password Requirements:</h4>
            <ul>
              <li className={getPasswordRequirements(newPassword).minLength ? styles.valid : styles.invalid}>
                At least 8 characters
              </li>
              <li className={getPasswordRequirements(newPassword).hasUpperCase ? styles.valid : styles.invalid}>
                At least one uppercase letter (A-Z)
              </li>
              <li className={getPasswordRequirements(newPassword).hasLowerCase ? styles.valid : styles.invalid}>
                At least one lowercase letter (a-z)
              </li>
              <li className={getPasswordRequirements(newPassword).hasNumber ? styles.valid : styles.invalid}>
                At least one number (0-9)
              </li>
              <li className={getPasswordRequirements(newPassword).hasSpecialChar ? styles.valid : styles.invalid}>
                At least one special character (!@#$%^&*(),.?":{}|&lt;&gt;)
              </li>
              <li className={getPasswordRequirements(newPassword).passwordsMatch ? styles.valid : styles.invalid}>
                Passwords match
              </li>
            </ul>
          </div>
        )}

        <button
          className={styles.saveButton}
          onClick={changePassword}
          disabled={isSaving}
        >
          <FontAwesomeIcon icon={faSave} /> Update Password
        </button>
      </div>

      {/* Two-Factor Authentication */}
      {/* <div className={styles.section}>
        <h3>
          <FontAwesomeIcon icon={faShieldAlt} /> Two-Factor Authentication
        </h3>
        <button className={styles.toggle2FAButton} onClick={toggle2FA}>
          {is2FAEnabled ? "Disable 2FA" : "Enable 2FA"}
        </button>
      </div> */}

      {/* Delete Account */}
      <div className={styles.section}>
        <h3>
          <FontAwesomeIcon icon={faTrash} /> Delete Account
        </h3>
        <button
          className={styles.deleteButton}
          onClick={() => setShowDeleteModal(true)}
          disabled={isSaving}
        >
          Delete My Account
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>Confirm Deletion</h3>
            <p>
              Are you sure you want to delete your account? This action will
              initiate the deletion process.
            </p>
            <div className={styles.disclaimerBox}>
              <p>
                <strong>Important:</strong> You have 15 days to reclaim your
                account if you change your mind by contacting{" "}
                <a href="mailto:support@waldiez.io">support@waldiez.io</a>.
                After this period, all your data will be permanently deleted.
              </p>
            </div>
            <div className={styles.inputGroup}>
              <label>Type "delete" to confirm:</label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="delete"
              />
            </div>
            <div className={styles.modalButtons}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText("");
                }}
              >
                Cancel
              </button>
              <button
                className={styles.confirmDeleteButton}
                onClick={deleteUserAccount}
                disabled={deleteConfirmText.toLowerCase() !== "delete"}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;
